import {
  listTimeTitleFunc,
  formatChartParamsDate,
} from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { echartsContent } from '@op/utils';
import {
  formatTimeXAxisText,
  getChartPosition,
  getChartsGradientColor,
} from '@op/utils/chart';
import { reviewsFormat, decimalFormatPercentage } from '@op/utils/value';
import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { detailPageTips } from '../../utils';
import {
  chartIconRich,
  getLegendFormat,
} from '../../MentionsSentiment/MetricsChart/Charts/utils';
import { textShowFun } from '../../MentionsSentiment/MetricsChart/utils';

export default function MentionsLineBarCharts({
  chartMap,
  filter: filterParams,
  seriesDataKeys,
  chartType,
  specialHeight,
  eventsDict,
  detailPage,
  defaultSelected,
}: any) {
  const realChartType = chartType === 'stack_line' ? 'line' : chartType;
  const { formatMessage } = useIntl();
  const [options, noData] = useMemo(() => {
    const series: {
      name: string;
      type: string;
      data: any[];
      [key: string]: any;
    }[] = [];
    let xAxis: string[] = [];
    let totalList: Array<number> = [];
    const minTotalList: Array<number> = [];
    const legends: Array<any> = [];

    const yAxis: Array<any> = [
      {
        type: 'value',
        splitNumber: 5,
        axisLabel: {
          formatter: (value: any) => echartsContent(value),
        },
      },
    ];

    seriesDataKeys.map(({ text, key, type, color, valueKey }: any) => {
      const data = chartMap[key];
      if (data) {
        const seriesObj: any = {
          name: text,
          type,
          barMaxWidth: 80,
          data: data.map((item: { [x: string]: any }) => item[valueKey]),
          itemStyle: { color },
        };
        if (valueKey !== 'score') {
          seriesObj.stack = 'total';
          totalList = data.map(
            (item: { [x: string]: number }, index: number) => {
              const total = totalList[index] || 0;
              return total + Math.abs(item[valueKey] || 0);
            },
          );
        } else {
          minTotalList.push(...seriesObj.data);
          seriesObj.yAxisIndex = 1;
          seriesObj.data = seriesObj?.data?.map(
            (value: number) => value || null,
          );
          yAxis.push({
            type: 'value',
            splitNumber: 5,
            max: 1,
            min: 0,
            axisLabel: {
              formatter: (value: any) => `${(value * 100).toFixed(0)}%`,
            },
          });
        }

        if (seriesObj.type === 'stack_line') {
          seriesObj.type = 'line';
          seriesObj.areaStyle = getChartsGradientColor(color, 0, 0.5);
        }

        legends.push({ name: text, itemStyle: { color } });
        series.push(seriesObj);

        if (xAxis.length === 0 && data) {
          xAxis = data.map((item: any) => item.time);
        }
      }
    });

    if (series.some((val) => val.type === 'bar') && xAxis?.length < 15) {
      series.push({
        name: 'Total',
        type: 'bar',
        stack: 'total',
        data: totalList.map(() => 0),
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) => totalList[params.dataIndex],
        },
        itemStyle: { color: 'rgba(0,0,0,0)' },
      });
    }

    const currentOptions = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) =>
          getChartPosition(pos, params, dom, rect, size, 50, 15, 'bottom', 1.2),
        appendToBody: false,
        confine: false,
        formatter: (params: any) => {
          const title = listTimeTitleFunc(
            params?.[0]?.axisValue,
            filterParams.date_type,
            true,
          );
          return formatChartParamsDate({
            params,
            title,
            beforeSort: false,
            textShowFun: textShowFun(formatMessage, { byType: 'by_rating' }),
            isFilterZero: true,
            valueFormat: (value, ele) => {
              if (ele.seriesName !== '30-Day Positive %') {
                return reviewsFormat(Math.abs(value));
              }

              return decimalFormatPercentage(value, '-', 0);
            },
            dealwithRecords(records) {
              const valueList = records.filter(
                (item) =>
                  !(
                    item.value.includes('%') || isNaN(Number(item.originValue))
                  ),
              );

              const total = valueList.reduce(
                (pre, cur) => pre + (cur?.originValue || 0),
                0,
              );
              records.unshift({
                text: formatMessage({ id: 'Mentions' }),
                value: String(reviewsFormat(total)),
                originValue: total,
                originText: 'Mentions',
              });
              return detailPageTips(records, formatMessage, detailPage);
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filterParams?.date_type),
      ),
      grid: {
        top: 20,
        right: 0,
        containLabel: true,
      },
      legend: {
        data: legends,
        formatter: getLegendFormat(formatMessage, { byType: 'by_rating' }),
        textStyle: { rich: chartIconRich },
        ...(defaultSelected ? { selected: defaultSelected } : {}),
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          showMaxLabel: true,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filterParams?.date_type),
        },
      },
      yAxis,
      series,
    };
    return [currentOptions, series.length === 0];
  }, [seriesDataKeys, chartMap, formatMessage, filterParams, defaultSelected]);

  return (
    <EChartCommon
      optionsProps={{ type: realChartType, excludeOptions: ['axisPointer'] }}
      className={`dbo-gamestore-chart-legend ${
        specialHeight === 0 ? 'dbo-gamestore-mentions-chart' : ''
      }`}
      height={210 + specialHeight}
      leftTitle={formatMessage({ id: 'Steam Added Reviews' })}
      rightTitle={
        chartType === 'bar' && formatMessage({ id: '30-Day Positive %' })
      }
      empty={noData}
      options={options}
      hasEmptyImg={false}
      eventsDict={eventsDict}
    ></EChartCommon>
  );
}
